import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { ref, push, set, onValue } from 'firebase/database';
import { db } from '../../util/firebase';
import styled from 'styled-components';

const FormContainer = styled.div`
  margin-top: 20px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 10px;
  font-size: 16px;

  &:focus {
    outline: none;
    border-color: #4A6CF7;
    box-shadow: 0 0 0 2px rgba(74, 108, 247, 0.2);
  }
`;

const Button = styled.button`
  background-color: #4A6CF7;
  color: white;
  padding: 12px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #3b5bdb;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  margin-top: 10px;
  font-size: 14px;
`;

const PlanInfo = styled.div`
  margin-top: 10px;
  font-size: 14px;
  color: #666;
`;

const Form = ({ onSubmit }) => {
  const [title, setTitle] = useState('');
  const [error, setError] = useState('');
  const [taskCount, setTaskCount] = useState(0);
  const [userPlan, setUserPlan] = useState('free');

  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (user) {
      // Get user's subscription plan
      const subscriptionRef = ref(db, `users/${user.uid}/subscription`);
      onValue(subscriptionRef, (snapshot) => {
        const subscription = snapshot.val();
        setUserPlan(subscription?.plan?.toLowerCase() || 'free');
      });

      // Get task count
      const todoListRef = ref(db, `users/${user.uid}/todoList`);
      onValue(todoListRef, (snapshot) => {
        const tasks = snapshot.val() || {};
        const activeTasks = Object.values(tasks).filter(task => (
          !task || (task.isCalendar !== true && !task.completed && !task.archived)
        ));
        setTaskCount(activeTasks.length);
      });
    }
  }, []);

  const createTodo = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      setError('You must be logged in to add tasks');
      return;
    }

    if (!title.trim()) {
      setError('Task title cannot be empty');
      return;
    }

    // Check task limits based on plan
    if (userPlan === 'free' && taskCount >= 5) {
      setError('Free plan is limited to 5 tasks. Upgrade to add more tasks.');
      return;
    }

    try {
      const todoListRef = ref(db, `users/${user.uid}/todoList`);
      const newTodoRef = push(todoListRef);
      const createdAt = Date.now();
      await set(newTodoRef, { title: title.trim(), createdAt, order: createdAt });

      setTitle('');
      setError('');
      onSubmit && await onSubmit(title);
    } catch (error) {
      setError('Error adding task. Please try again.');
      console.error("Error adding todo:", error);
    }
  };

  return (
    <FormContainer>
      <Input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Add a new task"
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            createTodo();
          }
        }}
      />
      <Button 
        onClick={createTodo}
        disabled={userPlan === 'free' && taskCount >= 5}
      >
        Add Task
      </Button>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      <PlanInfo>
        {userPlan === 'free' ? (
          <span>Free Plan: {taskCount}/5 tasks used</span>
        ) : (
          <span>Premium Plan: Unlimited tasks</span>
        )}
      </PlanInfo>
    </FormContainer>
  );
};

export default Form;
